import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorLogin - The login-required error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartDiscountErrorLogin',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorLogin } =
      this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;
    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    discountCode.value = discountCodeValue;

    this.querySelector('[data-discount-request-error]')?.classList.add('hidden');
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountErrorLogin.classList.add('hidden');

    if (this.dataset.customerSignedIn !== 'true') {
      try {
        sessionStorage.setItem('ruvinePendingDiscountCode', discountCodeValue.trim());
      } catch (error) {
        // Continue to the login prompt when browser storage is unavailable.
      }
      this.#handleDiscountError('login');
      return;
    }

    const abortController = this.#createAbortController();
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) { submitButton.disabled = true; submitButton.classList.add('is-loading'); }
    form.setAttribute('aria-busy', 'true');
    const timeout = setTimeout(() => abortController.abort(), 12000);

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some(code => code.toLowerCase() === discountCodeValue.toLowerCase())) return;

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      if (!response.ok) throw new Error('Cart update failed');

      if (!Array.isArray(data.discount_codes) || !data.discount_codes.some(discount => discount.code?.toLowerCase() === discountCodeValue.toLowerCase())) {
        this.#handleDiscountError('discount_code');
        return;
      }

      if (
        (data.discount_codes || []).find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code?.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false;
        })
      ) {
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') throw new Error('Cart summary unavailable');
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          (data.discount_codes || []).find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code?.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      this.#handleDiscountError('request');
    } finally {
      clearTimeout(timeout);
      if (submitButton) { submitButton.disabled = false; submitButton.classList.remove('is-loading'); }
      form.removeAttribute('aria-busy');
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      if (!response.ok) throw new Error('Cart update failed');

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'login' | 'request'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorLogin } =
      this.refs;
    const requestError = this.querySelector('[data-discount-request-error]');
    requestError?.classList.add('hidden');
    const target =
      type === 'request' ? requestError : type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : type === 'shipping'
          ? cartDiscountErrorShipping
          : cartDiscountErrorLogin;
    cartDiscountError.classList.remove('hidden');
    target?.classList.remove('hidden');
    cartDiscountError.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
